/**
 * Data shaping utilities for transforming query results into chart data.
 * Handles grouping, aggregation, and series transformations.
 */

import type {
  ChartConfig,
  ChartDataPoint,
  TransformedChartData,
  AggregationFunction,
  ColumnDataCategory,
} from '../types/chartTypes';
import { CHART_RAW_X_KEY } from '../types/chartTypes';
import { isNumericValue } from './columnTypeDetection';

/**
 * Extracts a numeric value from an unknown value
 */
export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return isFinite(value) ? value : null;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Parses a value as a Date. Only used when the column is known to be a date type.
 */
function tryParseDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) return date;
  }
  return null;
}

/**
 * Checks if a date has a meaningful time component (not midnight).
 *
 * Milliseconds count: a sub-second offset is TZ-independent, so a column of
 * `…T00:00:00.500Z` instants reads as time-grained in every timezone. Without
 * the ms check it looks like plain midnight wherever local midnight coincides
 * with UTC midnight (TZ=UTC), and `rawXFilterValue` degrades the instant to a
 * bare day — a cross-filter that no longer matches the row that was clicked.
 */
function hasTimeComponent(date: Date): boolean {
  return (
    date.getHours() !== 0 ||
    date.getMinutes() !== 0 ||
    date.getSeconds() !== 0 ||
    date.getMilliseconds() !== 0
  );
}

/**
 * Formats a date value for display, omitting time if not needed
 */
function formatDateValue(date: Date, includeTime: boolean): string {
  if (includeTime) {
    // Format: "Jan 15, 2024 14:30"
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  // Format: "Jan 15, 2024"
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a value for display as X-axis label.
 * Date formatting only applies when isDate is true (column is a known date type).
 */
export function formatXValue(
  value: unknown,
  includeTime = false,
  isDate = false
): string {
  if (value === null || value === undefined) return '(null)';

  if (isDate) {
    const date = tryParseDate(value);
    if (date) {
      return formatDateValue(date, includeTime);
    }
  }

  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const BARE_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Exact UTC midnight — the shape SQL DATE columns land in after the repo's
 *  UTC-anchored driver read-back (and `new Date('2026-03-10')`). */
function isUtcMidnight(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/**
 * The machine-value counterpart of `formatXValue` (S11 mark contract, C3):
 * what a cross-filter should bind when a mark with this x value is clicked.
 * Date axes encode GRANULARITY: day values → a bare `YYYY-MM-DD` (composed
 * downstream as a UTC-anchored [day, day+1) range per the S10 convention);
 * time-grained → the exact ISO instant (equality). Non-date values pass
 * through.
 *
 * C3-R2 (Codex 4): day-ness is decided from the RAW values, UTC-anchored —
 * never from the LOCAL clock (east of Greenwich, a DATE column's UTC
 * midnights look like 01:00 local, so the display heuristic
 * `shouldShowTimeForColumn` misreads the column as time-grained and the old
 * path emitted ISO-midnight equality that misses every non-midnight row on
 * a DATETIME-bound target tile). A bare `YYYY-MM-DD` string is always a
 * day; a Date value is a day when the WHOLE COLUMN is UTC midnights
 * (`dayValued`, column-scoped so the genuine 00:00 bucket of an hourly
 * chart keeps instant equality). Time-grained-but-day-GROUPED values keep
 * the label's local day.
 */
function rawXFilterValue(
  value: unknown,
  xIsDate: boolean,
  includeTime: boolean,
  dayValued: boolean,
): string | number | null {
  if (value === null || value === undefined) return null;
  if (xIsDate) {
    if (typeof value === 'string' && BARE_DAY_RE.test(value.trim())) return value.trim();
    const date = tryParseDate(value);
    if (date) {
      if (dayValued) return date.toISOString().slice(0, 10);
      if (includeTime) return date.toISOString();
      // Local getters ON PURPOSE: the label shows the local day, so the
      // filter must target the same day the user clicked. (Residual: rows
      // whose UTC day differs from the local label day near midnight.)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
  }
  if (typeof value === 'number') return value;
  return String(value);
}

/** True when every parseable date in the column sits at exactly UTC midnight
 *  — the shape a SQL DATE column has after UTC-anchored read-back. */
function columnIsDayValued(rows: Record<string, unknown>[], xColumn: string): boolean {
  let sawDate = false;
  for (const row of rows) {
    const value = row[xColumn];
    if (value === null || value === undefined) continue;
    const date = tryParseDate(value);
    if (!date) continue;
    sawDate = true;
    if (!isUtcMidnight(date)) return false;
  }
  return sawDate;
}

/** First raw x value per formatted label — one representative per group. */
function buildRawXByLabel(
  rows: Record<string, unknown>[],
  xColumn: string,
  includeTimeInX: boolean,
  xIsDate: boolean,
): Map<string, string | number | null> {
  const rawXByLabel = new Map<string, string | number | null>();
  const dayValued = xIsDate && columnIsDayValued(rows, xColumn);
  for (const row of rows) {
    const label = formatXValue(row[xColumn], includeTimeInX, xIsDate);
    if (!rawXByLabel.has(label)) {
      rawXByLabel.set(label, rawXFilterValue(row[xColumn], xIsDate, includeTimeInX, dayValued));
    }
  }
  return rawXByLabel;
}

/**
 * Analyzes a column's values to determine if any dates have time components.
 * Only meaningful for columns known to be date type.
 */
export function shouldShowTimeForColumn(
  rows: Record<string, unknown>[],
  column: string,
  isDate: boolean
): boolean {
  if (!isDate) return false;
  for (const row of rows) {
    const date = tryParseDate(row[column]);
    if (date && hasTimeComponent(date)) {
      return true;
    }
  }
  return false;
}

/**
 * Applies an aggregation function to an array of numbers
 */
export function aggregate(
  values: (number | null)[],
  fn: AggregationFunction
): number | null {
  const nums = values.filter((v): v is number => v !== null);

  if (nums.length === 0) return null;

  switch (fn) {
    case 'sum':
      return nums.reduce((a, b) => a + b, 0);
    case 'avg':
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min':
      return Math.min(...nums);
    case 'max':
      return Math.max(...nums);
    case 'count':
      return nums.length;
    case 'none':
      return nums[0] ?? null;
    default:
      return nums[0] ?? null;
  }
}

/**
 * Groups rows by X-axis value and optionally by series
 */
type GroupedData = Map<string, Map<string, (number | null)[]>>;

function groupRows(
  rows: Record<string, unknown>[],
  xColumn: string,
  yColumn: string,
  seriesColumn?: string,
  includeTimeInX = false,
  xIsDate = false
): GroupedData {
  const grouped: GroupedData = new Map();

  for (const row of rows) {
    const xValue = formatXValue(row[xColumn], includeTimeInX, xIsDate);
    const yValue = toNumber(row[yColumn]);
    const seriesValue = seriesColumn ? formatXValue(row[seriesColumn]) : yColumn;

    if (!grouped.has(xValue)) {
      grouped.set(xValue, new Map());
    }

    const xGroup = grouped.get(xValue)!;
    if (!xGroup.has(seriesValue)) {
      xGroup.set(seriesValue, []);
    }

    xGroup.get(seriesValue)!.push(yValue);
  }

  return grouped;
}

/**
 * Transforms raw query data into chart-ready format.
 * @param xColumnCategory - The detected data category of the X column.
 *   Date formatting is only applied when this is 'date'.
 */
export function transformChartData(
  rows: Record<string, unknown>[],
  config: ChartConfig,
  xColumnCategory: ColumnDataCategory = 'unknown'
): TransformedChartData {
  if (rows.length === 0 || !config.xAxis?.column || config.yAxis.length === 0) {
    return { data: [], seriesKeys: [], xDataKey: 'x', xIsNumeric: false };
  }

  const xColumn = config.xAxis.column;
  const seriesColumn = config.series?.column;
  const xDataKey = config.xAxis.label || xColumn;
  const xIsDate = xColumnCategory === 'date';

  // Detect if X column dates have meaningful time components
  const includeTimeInX = shouldShowTimeForColumn(rows, xColumn, xIsDate);

  // Machine value per label group for the S11 mark contract.
  const rawXByLabel = buildRawXByLabel(rows, xColumn, includeTimeInX, xIsDate);

  // Collect all series keys
  const seriesKeysSet = new Set<string>();

  // If we have a series column, group by it; otherwise use Y columns as series
  if (seriesColumn && config.yAxis.length > 1) {
    // Path C: Multiple Y columns + series grouping → compound series keys
    const data = new Map<string, ChartDataPoint>();

    for (const yCol of config.yAxis) {
      const grouped = groupRows(rows, xColumn, yCol.column, seriesColumn, includeTimeInX, xIsDate);
      const yLabel = yCol.label || yCol.column;

      for (const [xValue, seriesMap] of grouped) {
        if (!data.has(xValue)) {
          data.set(xValue, { [xDataKey]: xValue, [CHART_RAW_X_KEY]: rawXByLabel.get(xValue) ?? null });
        }
        const point = data.get(xValue)!;

        for (const [seriesValue, values] of seriesMap) {
          const compoundKey = `${yLabel} (${seriesValue})`;
          seriesKeysSet.add(compoundKey);
          point[compoundKey] = aggregate(values, yCol.aggregation);
        }
      }
    }

    return {
      data: Array.from(data.values()),
      seriesKeys: Array.from(seriesKeysSet),
      xDataKey,
      xIsNumeric: isAllNumeric(rows, xColumn),
    };
  } else if (seriesColumn) {
    // Path A: Single Y column with series grouping
    const yCol = config.yAxis[0];
    const grouped = groupRows(rows, xColumn, yCol.column, seriesColumn, includeTimeInX, xIsDate);

    const data: ChartDataPoint[] = [];

    for (const [xValue, seriesMap] of grouped) {
      const point: ChartDataPoint = {
        [xDataKey]: xValue,
        [CHART_RAW_X_KEY]: rawXByLabel.get(xValue) ?? null,
      };

      for (const [seriesValue, values] of seriesMap) {
        seriesKeysSet.add(seriesValue);
        point[seriesValue] = aggregate(values, yCol.aggregation);
      }

      data.push(point);
    }

    return {
      data,
      seriesKeys: Array.from(seriesKeysSet),
      xDataKey,
      xIsNumeric: isAllNumeric(rows, xColumn),
    };
  } else {
    // Multiple Y columns as series (no series grouping column)
    const grouped = new Map<string, Map<string, (number | null)[]>>();

    for (const yCol of config.yAxis) {
      const colGrouped = groupRows(rows, xColumn, yCol.column, undefined, includeTimeInX, xIsDate);
      for (const [xValue, seriesMap] of colGrouped) {
        if (!grouped.has(xValue)) {
          grouped.set(xValue, new Map());
        }
        const xGroup = grouped.get(xValue)!;
        for (const [key, values] of seriesMap) {
          const seriesKey = yCol.label || key;
          seriesKeysSet.add(seriesKey);
          xGroup.set(seriesKey, values);
        }
      }
    }

    const data: ChartDataPoint[] = [];

    for (const [xValue, seriesMap] of grouped) {
      const point: ChartDataPoint = {
        [xDataKey]: xValue,
        [CHART_RAW_X_KEY]: rawXByLabel.get(xValue) ?? null,
      };

      let yIndex = 0;
      for (const [seriesKey, values] of seriesMap) {
        const agg = config.yAxis[yIndex]?.aggregation || 'none';
        point[seriesKey] = aggregate(values, agg);
        yIndex++;
      }

      data.push(point);
    }

    return {
      data,
      seriesKeys: Array.from(seriesKeysSet),
      xDataKey,
      xIsNumeric: isAllNumeric(rows, xColumn),
    };
  }
}

/**
 * Checks if all non-null values in a column are numeric
 */
function isAllNumeric(
  rows: Record<string, unknown>[],
  column: string
): boolean {
  for (const row of rows) {
    const value = row[column];
    if (value !== null && value !== undefined) {
      if (!isNumericValue(value)) return false;
    }
  }
  return true;
}

/**
 * Limits data points to avoid performance issues with large datasets
 */
export function sampleData(
  rows: Record<string, unknown>[],
  maxPoints: number
): { sampled: Record<string, unknown>[]; wasSampled: boolean } {
  if (rows.length <= maxPoints) {
    return { sampled: rows, wasSampled: false };
  }

  // Use systematic sampling (every Nth row)
  const step = Math.ceil(rows.length / maxPoints);
  const sampled: Record<string, unknown>[] = [];

  for (let i = 0; i < rows.length && sampled.length < maxPoints; i += step) {
    sampled.push(rows[i]);
  }

  return { sampled, wasSampled: true };
}

/** Maximum recommended data points for chart performance */
export const MAX_CHART_DATA_POINTS = 1000;

/**
 * Transforms data for pie charts (special handling for single-value aggregation)
 */
export function transformPieChartData(
  rows: Record<string, unknown>[],
  config: ChartConfig,
  xColumnCategory: ColumnDataCategory = 'unknown'
): TransformedChartData {
  if (rows.length === 0 || !config.xAxis?.column || config.yAxis.length === 0) {
    return { data: [], seriesKeys: [], xDataKey: 'name', xIsNumeric: false };
  }

  const xColumn = config.xAxis.column;
  const yCol = config.yAxis[0];
  const xIsDate = xColumnCategory === 'date';

  // Detect if X column dates have meaningful time components
  const includeTimeInX = shouldShowTimeForColumn(rows, xColumn, xIsDate);
  const grouped = groupRows(rows, xColumn, yCol.column, undefined, includeTimeInX, xIsDate);
  // C3-R2 (Codex 3): pie slices carry the machine x value too — a
  // date-grouped pie must cross-filter on the day, not its locale label.
  const rawXByLabel = buildRawXByLabel(rows, xColumn, includeTimeInX, xIsDate);

  const data: ChartDataPoint[] = [];
  const valueKey = yCol.label || yCol.column;

  for (const [xValue, seriesMap] of grouped) {
    const values = seriesMap.get(yCol.column) || [];
    const aggregatedValue = aggregate(values, yCol.aggregation);

    if (aggregatedValue !== null) {
      data.push({
        name: xValue,
        [valueKey]: aggregatedValue,
        [CHART_RAW_X_KEY]: rawXByLabel.get(xValue) ?? null,
      });
    }
  }

  return {
    data,
    seriesKeys: [valueKey],
    xDataKey: 'name',
    xIsNumeric: false,
  };
}

/**
 * Transforms data for scatter charts (requires numeric X and Y)
 */
export function transformScatterChartData(
  rows: Record<string, unknown>[],
  config: ChartConfig
): TransformedChartData {
  if (rows.length === 0 || !config.xAxis?.column || config.yAxis.length === 0) {
    return { data: [], seriesKeys: [], xDataKey: 'x', xIsNumeric: true };
  }

  const xColumn = config.xAxis.column;
  const yCol = config.yAxis[0];
  const seriesColumn = config.series?.column;

  const seriesKeysSet = new Set<string>();
  const data: ChartDataPoint[] = [];

  for (const row of rows) {
    const xValue = toNumber(row[xColumn]);
    const yValue = toNumber(row[yCol.column]);

    if (xValue === null || yValue === null) continue;

    const seriesValue = seriesColumn
      ? formatXValue(row[seriesColumn])
      : yCol.label || yCol.column;

    seriesKeysSet.add(seriesValue);

    data.push({
      x: xValue,
      y: yValue,
      series: seriesValue,
    });
  }

  return {
    data,
    seriesKeys: Array.from(seriesKeysSet),
    xDataKey: 'x',
    xIsNumeric: true,
  };
}
